import React, { FC } from 'react';
import { navigate } from 'gatsby';
import { Button } from '@design-system/button';
import { Container } from '@design-system/container';
import { ExtendableImage } from '@design-system/extendable-image';
import { Typography } from '@design-system/typography';
import useJoiValidation from '@local-husky/hooks/useJoiValidation';
import { DangerouslySetInnerHtml } from '@phx-husky/dangerously-set-inner-html';
import { gatsbyImageMapper } from '@theme-husky/mappers/image';
import classNames from 'classnames';

import { IProductCardWithTextSectionProps } from './models';
import { productCardWithTextSectionSchema } from './ProductCardWithText.schema';

import './ProductCardWithText.scss';

const ProductCardWithText: FC<IProductCardWithTextSectionProps> = ({
  productName,
  image,
  imageAlt,
  cardStyle,
  buttonLink,
  buttonText,
  iconOne,
  iconOneAlt,
  iconOneText,
  iconTwo,
  iconTwoAlt,
  iconTwoText,
  iconThree,
  iconThreeAlt,
  iconThreeText,
  text,
}) => {
  const {
    data,
  }: {
    data: IProductCardWithTextSectionProps;
  } = useJoiValidation(productCardWithTextSectionSchema, 'ProductCardWithTextSection', {
    text,
    productName,
    image,
    imageAlt,
    cardStyle,
    buttonLink,
    buttonText,
    iconOne,
    iconOneAlt,
    iconOneText,
    iconTwo,
    iconTwoAlt,
    iconTwoText,
    iconThree,
    iconThreeAlt,
    iconThreeText,
  });

  const icons = [
    { icon: iconOne, alt: iconOneAlt, text: iconOneText },
    { icon: iconTwo, alt: iconTwoAlt, text: iconTwoText },
    { icon: iconThree, alt: iconThreeAlt, text: iconThreeText },
  ];

  return (
    <div className="product-cards-with-text-section">
      <Container maxWidth="max-grid" className="product-cards-with-text-section__container">
        <div
          className={classNames('finish-products-cards__card', {
            [`finish-products-cards__card--style-${cardStyle}`]: cardStyle,
          })}
        >
          <DangerouslySetInnerHtml
            html={productName}
            className="finish-products-cards__card-name"
          />

          <ul className="finish-products-cards__card-icons">
            {icons.map((icon, i) => (
              <li className="finish-products-cards__card-icon" key={icon.alt || `card-icon-${i}`}>
                <ExtendableImage
                  element={gatsbyImageMapper({
                    alt: icon.alt,
                    image: icon.icon,
                    objectFit: 'contain',
                  })}
                />
                <DangerouslySetInnerHtml
                  html={icon.text}
                  className="finish-products-cards__image-name"
                />
              </li>
            ))}
          </ul>

          {buttonLink && buttonText && (
            <div className="finish-products-cards__card-link-wrapper">
              <Button
                variant="primary"
                className="finish-products-cards__card-link"
                onClick={() => navigate(buttonLink)}
              >
                {buttonText}
              </Button>
            </div>
          )}

          <div className="finish-products-cards__card-image">
            <ExtendableImage
              element={gatsbyImageMapper({
                alt: imageAlt,
                image,
                objectFit: 'contain',
              })}
            />
          </div>
        </div>
        <div className="product-cards-with-text-section__wrapper">
          <Typography
            className="product-cards-with-text-section__text"
            variant="body"
            size="m"
            content={data.text}
            wrapping="default"
          />
        </div>
      </Container>
    </div>
  );
};
export default ProductCardWithText;
